import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Button from '../components/common/Button';
import CheckboxDropdownField from '../components/common/CheckboxDropdownField';
import CustomSelect from '../components/common/CustomSelect';
import { publicHiringStatus } from '../data/hiringStatus';
import PageHero from '../components/layout/PageHero';
import { getApplyForm, submitApplication } from '../services/applicationService';

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phonePattern = /^[0-9+\-\s()]{7,20}$/;

function getFieldOptions(field) {
  return Array.isArray(field.options) ? field.options.filter((option) => String(option || '').trim()) : [];
}

function getInitialResponses(form) {
  const responses = {};

  (form?.categories || []).forEach((category) => {
    category.fields.forEach((field) => {
      responses[field.key] = field.type === 'checkbox' ? [] : '';
    });
  });

  return responses;
}

function validateField(field, value, file) {
  if (field.type === 'file') {
    if (field.required && !file) {
      return `${field.label} is required.`;
    }

    if (!file) {
      return '';
    }

    const maxBytes = (Number(field.maxFileSizeMB) || 10) * 1024 * 1024;
    const extension = file.name.split('.').pop().toLowerCase();
    const allowedTypes = field.allowedFileTypes || [];

    if (file.size > maxBytes) {
      return `${field.label} must be ${field.maxFileSizeMB || 10}MB or smaller.`;
    }

    if (allowedTypes.length && !allowedTypes.includes(extension)) {
      return `${field.label} must be one of: ${allowedTypes.join(', ')}.`;
    }

    return '';
  }

  if (field.required && (Array.isArray(value) ? value.length === 0 : !String(value || '').trim())) {
    return `${field.label} is required.`;
  }

  if (!value) {
    return '';
  }

  if (field.type === 'email' && !emailPattern.test(value)) {
    return `${field.label} must be a valid email address.`;
  }

  if (field.type === 'phone' && !phonePattern.test(value)) {
    return `${field.label} must be a valid phone number.`;
  }

  return '';
}

function ApplyPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const jobSlug = searchParams.get('job') || '';
  const [state, setState] = useState({
    loading: true,
    error: '',
    form: null,
    hiringStatus: publicHiringStatus,
    selectedJob: null,
  });
  const [responses, setResponses] = useState({});
  const [files, setFiles] = useState({});
  const [errors, setErrors] = useState({});
  const [feedback, setFeedback] = useState({ type: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let isMounted = true;

    getApplyForm(jobSlug)
      .then((data) => {
        if (!isMounted) {
          return;
        }

        setState({
          loading: false,
          error: '',
          form: data.form,
          hiringStatus: data.hiringStatus || publicHiringStatus,
          selectedJob: data.selectedJob || null,
        });
        setResponses(getInitialResponses(data.form));
      })
      .catch(() => {
        if (isMounted) {
          setState({
            loading: false,
            error: 'Unable to load the application form right now.',
            form: null,
            hiringStatus: publicHiringStatus,
            selectedJob: null,
          });
        }
      });

    return () => {
      isMounted = false;
    };
  }, [jobSlug]);

  const activeFields = useMemo(
    () => (state.form?.categories || []).flatMap((category) => category.fields || []),
    [state.form]
  );

  const acceptingApplications = state.hiringStatus?.acceptingApplications === true;
  const closedMessage = state.hiringStatus?.closedMessage || publicHiringStatus.closedMessage;
  const closedLabel = state.hiringStatus?.closedLabel || publicHiringStatus.closedLabel;

  const updateResponse = (field, value) => {
    setResponses((current) => ({ ...current, [field.key]: value }));
    setErrors((current) => ({ ...current, [field.key]: '' }));
  };

  const updateFile = (field, file) => {
    setFiles((current) => ({ ...current, [field.key]: file }));
    setErrors((current) => ({ ...current, [field.key]: '' }));
  };

  const validateForm = () => {
    const nextErrors = {};

    activeFields.forEach((field) => {
      const error = validateField(field, responses[field.key], files[field.key]);
      if (error) {
        nextErrors[field.key] = error;
      }
    });

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFeedback({ type: '', message: '' });

    if (!acceptingApplications) {
      setFeedback({ type: 'error', message: closedMessage });
      return;
    }

    if (!validateForm()) {
      setFeedback({ type: 'error', message: 'Please fix the highlighted fields before submitting.' });
      return;
    }

    setSubmitting(true);
    try {
      const result = await submitApplication({
        jobSlug,
        jobTitle: state.selectedJob?.title || '',
        responses,
        files,
      });
      setResponses(getInitialResponses(state.form));
      setFiles({});
      const params = result.applicationId ? `?applicationId=${encodeURIComponent(result.applicationId)}` : '';
      navigate(`/apply/thank-you${params}`, {
        replace: true,
        state: {
          jobTitle: result.jobTitle || state.selectedJob?.title || '',
        },
      });
    } catch (error) {
      setFeedback({
        type: 'error',
        message: error.response?.data?.message || 'Unable to submit your application right now.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field) => {
    const commonProps = {
      id: field.key,
      name: field.key,
      disabled: !acceptingApplications,
      onChange: (event) => updateResponse(field, event.target.value),
      placeholder: field.placeholder,
      required: field.required,
      value: responses[field.key] || '',
    };

    if (field.type === 'textarea') {
      return <textarea {...commonProps} rows="5" />;
    }

    if (field.type === 'dropdown' || field.type === 'select') {
      const options = getFieldOptions(field);

      return (
        <CustomSelect
          id={field.key}
          name={field.key}
          disabled={!acceptingApplications}
          onChange={(value) => updateResponse(field, value)}
          options={options}
          placeholder="Select an option"
          required={field.required}
          value={responses[field.key] || ''}
        />
      );
    }

    if (field.type === 'radio') {
      const options = getFieldOptions(field);

      return (
        <div className="apply-option-list">
          {options.length === 0 ? <small>This field is not configured yet.</small> : null}
          {options.map((option, index) => (
            <label className="apply-option" key={`${field.key}-${option}-${index}`}>
              <input
                checked={responses[field.key] === option}
                disabled={!acceptingApplications}
                name={field.key}
                onChange={() => updateResponse(field, option)}
                type="radio"
                value={option}
              />
              {option}
            </label>
          ))}
        </div>
      );
    }

    if (field.type === 'checkbox') {
      return (
        <CheckboxDropdownField
          field={field}
          disabled={!acceptingApplications}
          onChange={(value) => updateResponse(field, value)}
          placeholder={field.placeholder || 'Select options'}
          value={responses[field.key] || []}
        />
      );
    }

    if (field.type === 'file') {
      const selectedFile = files[field.key];

      return (
        <div className="apply-file-upload">
          <input
            accept={(field.allowedFileTypes || []).map((type) => `.${type}`).join(',')}
            className="sr-only"
            disabled={!acceptingApplications}
            id={field.key}
            name={field.key}
            onChange={(event) => updateFile(field, event.target.files?.[0] || null)}
            required={field.required}
            type="file"
          />
          <label className="apply-file-upload__button" htmlFor={field.key}>
            Choose file
          </label>
          <span className="apply-file-upload__name">{selectedFile?.name || 'No file chosen'}</span>
        </div>
      );
    }

    const inputType = field.type === 'phone' ? 'tel' : field.type;
    return <input {...commonProps} type={inputType} />;
  };

  return (
    <>
      <PageHero
        eyebrow="CAREERS"
        title="Apply Now"
        description="Complete the FactorStreet job application form so our team can review your profile with the right context."
      />

      <section className="section apply-page">
        <div className="container apply-layout">
          <aside className="apply-sidebar card">
            <div className="apply-sidebar__copy">
              <span className="hiring-status-badge hiring-status-badge--closed">OUR HIRING IS CLOSED NOW</span>
              <p>{closedMessage}</p>
            </div>
            <div className="apply-sidebar__actions">
              <Button to="/jobs" variant="secondary">
                View Current Openings
              </Button>
              <Button to="/hiring-process" variant="secondary">
                Hiring Process
              </Button>
            </div>
          </aside>

          <div className="apply-form-shell">
            {state.loading ? <p className="jobs-empty">Loading application form...</p> : null}
            {state.error ? <p className="form-alert form-alert--error">{state.error}</p> : null}
            {!state.loading && !state.form ? (
              <div className="jobs-empty-card">
                <h2>Application form is not available right now.</h2>
                <p>Please check back later or contact us.</p>
                <Button to="/contact">Contact Us</Button>
              </div>
            ) : null}

            {state.form ? (
              <form className="apply-form" onSubmit={handleSubmit}>
                <div className="apply-form__header">
                  <p className="eyebrow">FACTOR STREET</p>
                  <h2>Job Application Form</h2>
                </div>

                {feedback.message ? (
                  <p className={`form-alert form-alert--${feedback.type === 'error' ? 'error' : 'success'}`}>
                    {feedback.message}
                  </p>
                ) : null}
                {!acceptingApplications ? <p className="form-alert form-alert--error">{closedMessage}</p> : null}

                <fieldset className="apply-form__fields" disabled={!acceptingApplications}>
                  <legend className="sr-only">{closedLabel}</legend>
                  {state.form.categories.map((category) => (
                    <section className="apply-category" key={category.id || category.slug}>
                      <div className="apply-category__header">
                        <h3>{category.title}</h3>
                        {category.description ? <p>{category.description}</p> : null}
                      </div>
                      <div className="apply-field-grid">
                        {category.fields.map((field) => (
                          <div
                            className={`apply-field${
                              field.type === 'checkbox' ||
                              field.type === 'textarea' ||
                              field.type === 'file' ||
                              field.type === 'url'
                                ? ' apply-field--full'
                                : ''
                            }`}
                            key={field.id || field.key}
                          >
                            <label htmlFor={field.key}>
                              {field.label}
                              {field.required ? <strong> *</strong> : null}
                            </label>
                            {renderField(field)}
                            {field.helpText ? <small>{field.helpText}</small> : null}
                            {errors[field.key] ? <small className="apply-field__error">{errors[field.key]}</small> : null}
                          </div>
                        ))}
                      </div>
                    </section>
                  ))}
                </fieldset>

                <button className="btn btn--primary apply-submit" disabled={submitting || !acceptingApplications} type="submit">
                  {submitting ? 'Submitting...' : acceptingApplications ? 'Submit Application' : 'Applications Closed'}
                </button>
              </form>
            ) : null}
          </div>
        </div>
      </section>
    </>
  );
}

export default ApplyPage;
